import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import ExpenseForm from '@components/Expenses/ExpenseForm';
import { CashFlow } from '@api/CashFlows/CashFlow';
import ExpenseServ from '@api/CashFlows/ExpenseServ';
import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';

interface ModalExpenseFormProps {
    open: boolean;
    /** Editar este gasto; sin él, registrar uno nuevo. */
    editing?: CashFlow;
    /** Sucursal sugerida para registrar (la del filtro del Informe). */
    defaultBranchOfficeId?: string;
    onClose: () => void;
    onSaved: () => void;
}

/**
 * "+ Registrar gasto" desde la pestaña Gastos (§5.1): para quien no está en el POS,
 * como el admin general capturando la renta de Cholula. Mismo formulario que el POS,
 * con selector de sucursal. También edita un gasto.
 */
const ModalExpenseForm: React.FunctionComponent<ModalExpenseFormProps> = ({
    open,
    editing,
    defaultBranchOfficeId,
    onClose,
    onSaved
}) => {
    const { branchOffices } = useSelector((state: RootReducer) => state.krispis);
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    // Las sucursales donde puede capturar (la lista del store ya viene acotada al usuario).
    const branches = useMemo(
        () =>
            branchOffices.filter((branch) =>
                canCurrentUser(Capability.ExpenseCaptureOperating, { branchOfficeId: branch.id })
            ),
        [branchOffices]
    );
    const initialBranch =
        defaultBranchOfficeId && defaultBranchOfficeId !== 'all'
            ? defaultBranchOfficeId
            : branchOfficeSelected?.id || branches[0]?.id;
    const [branchOfficeId, setBranchOfficeId] = useState<string | undefined>(initialBranch);
    const [amountLocked, setAmountLocked] = useState(false);

    useEffect(() => {
        if (open) setBranchOfficeId(editing?.branchOfficeId || initialBranch);
    }, [open, editing]);

    // ¿La caja de este gasto ya cerró? Entonces el monto no se edita (§4.1).
    useEffect(() => {
        setAmountLocked(false);
        if (!open || !editing) return;
        let alive = true;
        void ExpenseServ.isAmountLocked(editing).then((locked) => alive && setAmountLocked(locked));
        return () => {
            alive = false;
        };
    }, [open, editing]);

    const onFormSaved = () => {
        onSaved();
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 15 } }}>
            <DialogTitle fontWeight={600}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {editing ? 'Editar gasto' : 'Registrar gasto'}
                    <IconButton onClick={onClose} aria-label="Cerrar">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
                {!editing && branches.length > 1 && (
                    <FormControl size="small" sx={{ mx: 3, mt: 1 }}>
                        <InputLabel id="expense-branch-label">Sucursal</InputLabel>
                        <Select
                            labelId="expense-branch-label"
                            label="Sucursal"
                            value={branchOfficeId || ''}
                            onChange={(event) => setBranchOfficeId(String(event.target.value))}
                        >
                            {branches.map((branch) => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                {open && (
                    <ExpenseForm
                        key={`${editing?.id || 'new'}-${branchOfficeId}`}
                        branchOfficeId={branchOfficeId}
                        editing={editing}
                        amountLocked={amountLocked}
                        onSaved={onFormSaved}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ModalExpenseForm;
